/**
 * Signature detection results, stored on FileVersion.signatureDetectionJson
 * and mirrored to DocumentCache for filtering.
 *
 * Every state transition on the JSON goes through a helper in this file: the
 * detection run, the status it applies, and the signer extraction step all
 * write the same shape, so keeping the transitions here stops them drifting
 * apart.
 */

export interface SignatureDetectionField {
  /**
   * Whether the region's pixels look like a signature rather than an empty
   * box. Apryse's confidence says the box *is* a signature field, not that
   * anyone signed it, so this is what separates a signed contract from a
   * blank form. A field whose pixels could not be measured is `false`: an
   * unmeasured box is not evidence that anyone signed.
   */
  appearsSigned: boolean;
  confidence: number;
  pageNumber: number;
  rect: [number, number, number, number];
  type: string;
  /** Share of the region's pixels that are ink. Recorded for tuning. */
  inkCoverage?: number;
  /** Spread of per-column ink height; the signal `appearsSigned` rests on. */
  strokeVariation?: number;
}

/** A person who signed the document, as read by the signer extraction step. */
export interface SignatureSigner {
  /** 0 to 1: how sure the model is that this person signed. */
  confidence: number;
  name: string;
  organization: string | null;
  /** 1-indexed page holding the signature, when the model could tell. */
  pageNumber: number | null;
  /** ISO date (YYYY-MM-DD) written next to the signature, when there is one. */
  signedDate: string | null;
  title: string | null;
  /**
   * Where the signer's printed name sits on `pageNumber`, `[x1, y1, x2, y2]` in
   * PDF points with a top-left origin, found by matching the name in the page
   * text. The signature is beside it, which makes this a better place to jump
   * to than a detected box. Null when the name is not printed on the page.
   */
  rect?: [number, number, number, number] | null;
}

/**
 * A signature read from the PDF's own signature dictionaries, with no
 * rendering engine involved.
 */
export interface DigitalSignature {
  /** The certificate that signed, as it names its holder. Null when none could be read. */
  certificate: DigitalSignatureCertificate | null;
  /**
   * Whether the signature covers the whole file. False when the file was
   * saved again after signing, which a later signature or a form fill does
   * legitimately; it only means the signature does not vouch for what came after.
   */
  coversWholeFile: boolean;
  /** The signature's format, its `/SubFilter`, such as `adbe.pkcs7.detached`. */
  format: string | null;
  integrity: DigitalSignatureIntegrity;
  /** `timestamp` for an RFC 3161 document timestamp, which certifies when, not who. */
  kind: DigitalSignatureKind;
  location: string | null;
  /** The signer's name as the signing software wrote it (`/Name`). */
  name: string | null;
  reason: string | null;
  /** When it was signed: a timestamp authority's time when there is one, otherwise the signer's clock. */
  signedAt: string | null;
}

export interface DigitalSignatureCertificate {
  commonName: string | null;
  email: string | null;
  /** Who issued the certificate, by common name, or organization when it has none. */
  issuer: string | null;
  organization: string | null;
}

export const DIGITAL_SIGNATURE_KINDS = {
  signature: 'signature',
  timestamp: 'timestamp',
} as const;

export type DigitalSignatureKind =
  (typeof DIGITAL_SIGNATURE_KINDS)[keyof typeof DIGITAL_SIGNATURE_KINDS];

/**
 * - `intact`: the signed bytes are unchanged and the signature verifies
 *   against its certificate. Says nothing about whether to trust that
 *   certificate: no chain or revocation is checked.
 * - `altered`: the signed bytes changed after signing.
 * - `rewritten`: the byte range points past the end of the file, so the bytes
 *   it signed are not in this file at all. A tool rewrote the file after
 *   signing, or copied the signature over when it split pages off a signed one.
 * - `unverified`: the check could not be completed (an unsupported algorithm,
 *   an encrypted or malformed signature); says nothing either way.
 */
export const DIGITAL_SIGNATURE_INTEGRITIES = {
  altered: 'altered',
  intact: 'intact',
  rewritten: 'rewritten',
  unverified: 'unverified',
} as const;

export type DigitalSignatureIntegrity =
  (typeof DIGITAL_SIGNATURE_INTEGRITIES)[keyof typeof DIGITAL_SIGNATURE_INTEGRITIES];

export interface SignatureDetectionJson {
  confidenceThreshold: number;
  detectedSignatureFields: SignatureDetectionField[];
  failedAt: string | null;
  failedReason: string | null;
  finishedAt: string | null;
  /** Which engine produced the run. */
  source: SignatureDetectionSource;
  startedAt: string | null;
  /**
   * Every signature and document timestamp read from the PDF's signature
   * dictionaries, in signing order. Read before the engine runs, so it is
   * there even when the engine fails. Absent on runs recorded before this
   * check existed.
   */
  digitalSignatures?: DigitalSignature[];
  /**
   * Whether the PDF carries a real cryptographic signature, read from the
   * AcroForm signature dictionaries rather than inferred from the page image.
   *
   * Optional because rows written before this field existed have no value for
   * it, and `undefined` there means "never checked" rather than "not signed".
   */
  hasCryptographicSignature?: boolean;
  /** The owner's rolling notification this version was counted in. Carried with `notifiedAt`. */
  notificationWindowId?: string | null;
  /**
   * When the owner was notified about this version's signature. Carried like
   * `statusAppliedAt`, so a re-run does not notify a second time.
   */
  notifiedAt?: string | null;
  /**
   * The workflow that started the run. While it is in flight, a different
   * workflow skips this version rather than scanning the same PDF twice; a
   * retry of the same workflow carries the same key and goes ahead.
   */
  runKey?: string | null;
  /** Signers read from the pages, filled after detection by an LLM step. */
  signers?: SignatureSigner[];
  signersExtractedAt?: string | null;
  signersFailedAt?: string | null;
  /**
   * When this run set the document to Signed. Set at most once per file
   * version and carried across re-runs of the same version, so a retry or a
   * backfill never flips a status the user has since changed back.
   */
  statusAppliedAt?: string | null;
}

/** The engines a detection run can come from, as stored in `source`. */
export const SIGNATURE_DETECTION_SOURCES = {
  apryse: 'apryse_form_detection',
  liteparse: 'liteparse',
} as const;

export type SignatureDetectionSource =
  (typeof SIGNATURE_DETECTION_SOURCES)[keyof typeof SIGNATURE_DETECTION_SOURCES];

/**
 * Minimum Apryse confidence for a detected region to be worth measuring.
 *
 * Apryse's confidence answers "is this a signature box and are the
 * coordinates right", not "is it signed", so it cannot carry the decision on
 * its own and only needs to be loose enough to admit real boxes. Measured
 * against a reference set of executed contracts and blank forms, the ink
 * check rejected every unsigned region on its own at this threshold, while
 * the previous 0.75 excluded two genuinely signed contracts whose boxes
 * scored 0.70 and 0.72.
 */
export const DEFAULT_SIGNATURE_CONFIDENCE_THRESHOLD = 0.65;

/**
 * How long a run may sit started without a terminal stamp before it counts as
 * dead. Well past the slowest real run (the Apryse pass gives up after eight
 * minutes, and the activity retries three times), so only a run whose worker
 * died lands here.
 */
export const SIGNATURE_DETECTION_STALE_AFTER_MS = 30 * 60 * 1000;

/**
 * Opens a detection run. Callers that own a FileVersion persist this before
 * the engine runs, so a worker that dies mid-run leaves the column as
 * started-but-never-finished, which `getNeedsSignatureDetection` treats as
 * eligible again rather than as a completed scan.
 */
export function createSignatureDetectionStartedJson(args: {
  confidenceThreshold: number;
  runKey?: string;
  startedAt?: string;
}): SignatureDetectionJson {
  const { confidenceThreshold, runKey, startedAt = new Date().toISOString() } = args;

  return {
    confidenceThreshold,
    detectedSignatureFields: [],
    failedAt: null,
    failedReason: null,
    finishedAt: null,
    runKey: runKey ?? null,
    source: SIGNATURE_DETECTION_SOURCES.apryse,
    startedAt,
  };
}

/**
 * Closes a run that reached the engine. An empty `detectedSignatureFields` with
 * a `finishedAt` is a real answer: no box cleared the confidence threshold.
 *
 * An empty list is not the only way to count as unsigned, and the filter does
 * not test its length. A field counts only when its pixels read as ink; a
 * cryptographic signature counts on its own even with no fields at all.
 * Simplifying the filter back to `detectedSignatureFields.length > 0` flags
 * every blank form again.
 */
export function createSignatureDetectionSucceededJson(
  started: SignatureDetectionJson,
  args: {
    detectedSignatureFields: SignatureDetectionField[];
    digitalSignatures?: DigitalSignature[];
    finishedAt?: string;
    hasCryptographicSignature?: boolean;
  }
): SignatureDetectionJson {
  const {
    detectedSignatureFields,
    digitalSignatures,
    finishedAt = new Date().toISOString(),
    hasCryptographicSignature,
  } = args;

  return {
    ...started,
    detectedSignatureFields,
    digitalSignatures,
    failedAt: null,
    failedReason: null,
    finishedAt,
    hasCryptographicSignature,
  };
}

/**
 * Closes a run that failed. Never fabricates fields: a failed run must not
 * count as a detected signature, so `detectedSignatureFields` stays empty and
 * `finishedAt` stays null.
 */
export function createSignatureDetectionFailedJson(
  started: SignatureDetectionJson,
  args: { failedReason: string; failedAt?: string }
): SignatureDetectionJson {
  const { failedAt = new Date().toISOString(), failedReason } = args;

  return {
    ...started,
    detectedSignatureFields: [],
    failedAt,
    failedReason,
    finishedAt: null,
  };
}

/**
 * Copies what a previous run of the same file version decided onto a new run.
 *
 * A re-run over the same bytes (an extraction retry, a threshold change, a
 * backfill pass) must not apply the status a second time: if the user undid
 * it, re-applying would silently overrule them. Signers carry over too, since
 * the pages they were read from have not changed.
 */
export function carrySignatureDetectionOutcome(
  previous: SignatureDetectionJson | null | undefined,
  next: SignatureDetectionJson
): SignatureDetectionJson {
  if (!previous) return next;

  return {
    ...next,
    notificationWindowId: previous.notificationWindowId,
    notifiedAt: previous.notifiedAt,
    signers: previous.signers,
    signersExtractedAt: previous.signersExtractedAt,
    signersFailedAt: previous.signersFailedAt,
    statusAppliedAt: previous.statusAppliedAt,
  };
}

/**
 * Records the outcome of the signer extraction step. `signers: null` means the
 * step failed; an empty list is a real answer (no legible signer).
 */
export function createSignatureDetectionSignersJson({
  at = new Date().toISOString(),
  json,
  signers,
}: {
  json: SignatureDetectionJson;
  signers: SignatureSigner[] | null;
  at?: string;
}): SignatureDetectionJson {
  if (!signers) return { ...json, signersFailedAt: at };
  return { ...json, signers, signersExtractedAt: at, signersFailedAt: null };
}

export function getIsDigitalSignatureIntegrity(value: string): value is DigitalSignatureIntegrity {
  return Object.values(DIGITAL_SIGNATURE_INTEGRITIES).some(integrity => integrity === value);
}

/**
 * Whether a digital signature shows this file was signed. A document
 * timestamp names no signer, and a `rewritten` signature signed bytes that are
 * not in this file: a page split off a signed agreement keeps its signature
 * dictionary without being signed itself.
 */
export function getDigitalSignatureCountsAsSigned(signature: DigitalSignature): boolean {
  return (
    signature.kind === DIGITAL_SIGNATURE_KINDS.signature &&
    signature.integrity !== DIGITAL_SIGNATURE_INTEGRITIES.rewritten
  );
}

/** True when a finished run found a signature: a cryptographic one, or an inked field. */
export function getIsSignatureDetected(json: SignatureDetectionJson | null | undefined): boolean {
  if (!json?.finishedAt) return false;
  if (json.hasCryptographicSignature) return true;
  return json.detectedSignatureFields.some(getFieldCountsAsSigned);
}

/**
 * True while a run is in flight, which the grid shows as "Ai detecting
 * status..." in place of the status menu. A run abandoned by a dead worker
 * never gets a terminal stamp, so past `SIGNATURE_DETECTION_STALE_AFTER_MS` it
 * stops counting, or the document's status could never be edited again.
 */
export function getIsSignatureDetectionRunning(
  json: Pick<SignatureDetectionJson, 'failedAt' | 'finishedAt' | 'startedAt'> | null | undefined,
  now = Date.now()
): boolean {
  if (!json?.startedAt) return false;
  if (json.finishedAt || json.failedAt) return false;
  return now - Date.parse(json.startedAt) < SIGNATURE_DETECTION_STALE_AFTER_MS;
}

/**
 * True while a different workflow's run of this version is in flight. An
 * upload and a backfill can both reach the same latest version; the second
 * skips it instead of running the engine on the same PDF again.
 */
export function getIsSignatureDetectionRunningElsewhere(
  json:
    | Pick<SignatureDetectionJson, 'failedAt' | 'finishedAt' | 'runKey' | 'startedAt'>
    | null
    | undefined,
  runKey: string | undefined,
  now = Date.now()
): boolean {
  if (!json?.runKey || json.runKey === runKey) return false;
  return getIsSignatureDetectionRunning(json, now);
}

export interface SignatureDetectionPendingInput {
  detection:
    | Pick<SignatureDetectionJson, 'failedAt' | 'finishedAt' | 'startedAt'>
    | null
    | undefined;
  /** The document's core AI stamps, by their GraphQL names, so a grid row or a document passes as is. */
  document: {
    coreAiPropertiesExtractionFailedAt: string | null;
    coreAiPropertiesExtractionFinishedAt: string | null;
    coreAiPropertiesExtractionStartedAt: string | null;
  };
  isEnabled: boolean;
  isPdf: boolean;
  now?: number;
}

/**
 * True while the document's status is still being decided, which the grid
 * shows as "Ai detecting status..." next to "Ai setting type...".
 *
 * Enrichment starts detection as its own workflow, which only stamps its start
 * once a detection slot is free, while core AI is stamped as started the
 * moment the file is uploaded and can finish before detection begins. So for
 * a PDF in an org with the beta on, detection counts as pending while core AI
 * runs and for `SIGNATURE_DETECTION_STALE_AFTER_MS` after it ends, until this
 * version has a result. A run that never starts stops showing after that.
 */
export function getIsSignatureDetectionPending({
  detection,
  document,
  isEnabled,
  isPdf,
  now = Date.now(),
}: SignatureDetectionPendingInput): boolean {
  if (getIsSignatureDetectionRunning(detection, now)) return true;
  if (!isEnabled || !isPdf) return false;
  if (detection?.finishedAt || detection?.failedAt) return false;
  if (!document.coreAiPropertiesExtractionStartedAt) return false;
  const coreAiEndedAt =
    document.coreAiPropertiesExtractionFinishedAt ?? document.coreAiPropertiesExtractionFailedAt;
  if (!coreAiEndedAt) return true;
  return now - Date.parse(coreAiEndedAt) < SIGNATURE_DETECTION_STALE_AFTER_MS;
}

/**
 * Whether a detected field counts as evidence that the document was signed.
 *
 * A field is only evidence of signing once its pixels have been measured and
 * show ink: Apryse locates signature *boxes*, and a blank form is full of
 * them.
 */
export function getFieldCountsAsSigned(field: SignatureDetectionField): boolean {
  return field.appearsSigned;
}

/**
 * True when a file version still needs a detection run, which is what the
 * backfill query selects on: no run, a run that died mid-flight (worker
 * restart, wedged native call), or a failed run. A backfill visits each
 * document once, so a broken PDF costs one attempt per pass, and a failure
 * that has since gone away (an outage, an engine fix) is picked up.
 */
export function getNeedsSignatureDetection(
  json: SignatureDetectionJson | null | undefined
): boolean {
  return !json?.finishedAt;
}
